import React, { useEffect } from "react";

import NarratorIntonation from "../intonation/NarratorIntonation";
import IntonationFeedbackDisplay from "../intonation/IntonationFeedbackDisplay";
import IntonationHelpModal from "../intonation/IntonationHelpModal";

const PitchGraphGuide = ({ onOpenHelp }) => (
<div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-blue-100 px-3 py-2 text-[12px]">
  <span
    className="inline-flex items-center gap-1.5 font-medium"
    style={{ color: "rgba(37,99,235,1)" }}
  >
    <span
      className="block w-4"
      style={{
        borderTop: "2.25px solid rgba(37,99,235,1)",
      }}
      aria-hidden="true"
    />
    Pitch
  </span>

  <span
    className="inline-flex items-center gap-1.5 font-medium"
    style={{ color: "rgba(100,116,139,0.72)" }}
  >
    <span
      className="block w-4"
      style={{
        borderTop: "1.15px solid rgba(100,116,139,0.38)",
      }}
      aria-hidden="true"
    />
    Intensity
  </span>
</div>
);

const IntonationComparisonPanel = ({
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  segment,
  assessment,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  audioref,
  visualizationView,
  rate,
  setRate,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
  fitViewport = false,
  compactViewport = false,
  userFirst = false,
  showRateMe = false,
  onRateMe,
}) => {
  const showUserFirst = Boolean(userFirst && assessment);
  const isPitchView = visualizationView === "pitch";

  useEffect(() => {
    if (!isPitchView && showIntonationHelp) {
      setShowIntonationHelp(false);
    }
  }, [isPitchView, showIntonationHelp, setShowIntonationHelp]);

  if (!showIntonationVisualization) return null;

  return (
    <section
      className={[
        "w-full overflow-hidden rounded-2xl border backdrop-blur-sm transition-[border-color,box-shadow,background-color] duration-300",
        showUserFirst
          ? "border-slate-200/80 bg-white shadow-lg shadow-slate-500/5"
          : "border-white/40 bg-white/80 shadow-lg shadow-blue-500/5",
        fitViewport
          ? "flex min-h-0 flex-1 flex-col"
          : "",
      ].join(" ")}
    >
      {showUserFirst && showRateMe && (
        <button
          type="button"
          onClick={onRateMe}
          className="group flex w-full shrink-0 items-center justify-between gap-3 border-b border-purple-700 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-2 text-left text-white shadow-md shadow-purple-500/15 transition-all hover:from-purple-700 hover:to-indigo-700 hover:shadow-lg hover:shadow-purple-500/20 active:scale-[0.998] sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/20 text-sm ring-1 ring-white/25"
              aria-hidden="true"
            >
              👥
            </span>

            <div className="truncate text-sm font-bold sm:text-base">
              Ask the community
            </div>
          </div>

          <span className="inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg bg-white px-3.5 py-1.5 text-sm font-bold text-purple-700 shadow-sm transition-transform group-hover:translate-x-0.5">
            Request feedback
            <span aria-hidden="true">→</span>
          </span>
        </button>
      )}

      <div
        className={[
          "flex flex-col",
          fitViewport
            ? "min-h-0 flex-1"
            : "",
        ].join(" ")}
      >
        {segment?.words?.length > 0 && (
          <div
            className={[
              showUserFirst
                ? "order-2 border-t border-slate-100 bg-slate-50/35"
                : "order-1",
              fitViewport
                ? "flex min-h-0 flex-1 flex-col"
                : "",
              compactViewport
                ? "p-3"
                : showUserFirst
                ? "p-3 sm:p-4"
                : "p-3 sm:p-4",
            ].join(" ")}
          >
            <div
              className={[
                "flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5",
                compactViewport ? "mb-2" : "mb-2.5",
              ].join(" ")}
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-xs text-white shadow-sm">
                🎭
              </div>

              <h4 className="font-semibold text-gray-800">
                Narrator
              </h4>

              <span className="rounded-full border border-blue-200/60 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700">
                Reference
              </span>

              {!showUserFirst && isPitchView && (
                <button
                  type="button"
                  onClick={() => setShowIntonationHelp(true)}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100"
                  aria-label="How to read the pitch and intensity graph"
                >
                  <span
                    className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] ring-1 ring-blue-200"
                    aria-hidden="true"
                  >
                    i
                  </span>
                  How to read this graph
                </button>
              )}

              {!showUserFirst && (
                <span className="ml-auto hidden whitespace-nowrap text-[11px] font-medium text-gray-400 lg:inline">
                  Press <span className="font-semibold">V</span> to change view
                </span>
              )}
            </div>

            <div
              className={[
                "rounded-xl bg-white/60 border border-white/50 overflow-hidden",
                fitViewport
                  ? "flex min-h-[220px] flex-1 flex-col"
                  : "min-h-[220px] sm:min-h-[240px]",
              ].join(" ")}
            >
              {!showUserFirst && isPitchView && (
                <PitchGraphGuide
                  onOpenHelp={() => setShowIntonationHelp(true)}
                />
              )}

              <div
                className={[
                  fitViewport
                    ? "flex min-h-0 flex-1"
                    : "",
                  compactViewport
                    ? "p-1.5 sm:p-2"
                    : "p-2 sm:p-3",
                ].join(" ")}
              >
                <NarratorIntonation
                  subject={subject}
                  file={file}
                  segment={segment}
                  onWordClick={handleLaneWordClick}
                  broadcastTime={broadcastTime}
                  audioRef={audioref}
                  view={visualizationView}
                  speed={rate}
                  fitViewport={fitViewport}
                />
              </div>

              {!fitViewport && (
                <div className="px-3 pb-3 pt-2 border-t border-white/40">
                  <div className="rounded-xl bg-white/60 border border-white/50 backdrop-blur-sm px-4 py-3 shadow-sm w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Tip:</span>{" "}
                        Click a <span className="font-semibold">word</span> to replay it.
                        If it feels fast, try <span className="font-semibold">0.25×</span>.
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-500">
                          Speed
                        </span>

                        <div className="inline-flex rounded-xl bg-white border border-gray-200 shadow-sm p-1">
                          {[0.25, 0.5, 1].map((speed) => {
                            const active = rate === speed;

                            return (
                              <button
                                key={speed}
                                onClick={() => {
                                  setRate(speed);
                                  if (audioref.current) {
                                    audioref.current.playbackRate = speed;
                                  }
                                }}
                                className={[
                                  "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                                  active
                                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                                    : "text-gray-600 hover:bg-gray-50",
                                ].join(" ")}
                              >
                                {speed}x
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {assessment && (
          <div
            className={[
              showUserFirst
                ? "order-1 bg-white p-3 sm:p-4"
                : "order-2 border-t border-white/25 p-4 sm:p-5",
            ].join(" ")}
          >
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-xs text-white shadow-sm">
                🎙️
              </div>

              <h4 className="font-semibold text-gray-800">
                Your recording
              </h4>

              {showUserFirst && isPitchView && (
                <button
                  type="button"
                  onClick={() => setShowIntonationHelp(true)}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100"
                  aria-label="How to read the pitch and intensity graph"
                >
                  <span
                    className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] ring-1 ring-blue-200"
                    aria-hidden="true"
                  >
                    i
                  </span>
                  How to read this graph
                </button>
              )}

              {!showUserFirst && (
                <span className="ml-auto hidden text-[11px] text-gray-500 md:inline">
                  Compare with the narrator
                </span>
              )}
            </div>

            <div
              className={[
                "min-h-[200px] overflow-hidden rounded-xl sm:min-h-[220px]",
                showUserFirst
                  ? "border border-slate-200/80 bg-white shadow-sm"
                  : "border border-white/50 bg-white/60",
              ].join(" ")}
            >
              {showUserFirst && isPitchView && (
                <PitchGraphGuide
                  onOpenHelp={() => setShowIntonationHelp(true)}
                />
              )}

              <div className="p-1.5 sm:p-2">
                <IntonationFeedbackDisplay
                  assessment={assessment}
                  segment={segment}
                  view={visualizationView}
                  isAdmin={auth?.role === "ADMIN"}
                  audioUrl={resolvedAssessmentAudioUrl}
                  narratorAudioUrl={resolvedNarratorAssessmentAudioUrl}
                />
              </div>
            </div>
          </div>
        )}

        {!assessment && !fitViewport && (
          <div className="order-2 border-t border-white/25 p-4 sm:p-5">
            <div className="rounded-xl border border-dashed border-gray-300/70 bg-white/50 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/70 border border-white/50 flex items-center justify-center">
                🎧
              </div>

              <div>
                <div className="font-semibold text-gray-800">
                  Record to see your curve here
                </div>

                <p className="text-sm text-gray-600">
                  After recording, you'll be able to replay your audio and compare
                  it with the narrator above.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <IntonationHelpModal
        open={isPitchView && showIntonationHelp}
        onClose={() => setShowIntonationHelp(false)}
      />
    </section>
  );
};

export default IntonationComparisonPanel;